import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Popconfirm, Skeleton } from 'antd';
import { ColumnProps } from 'antd/lib/table';
import React from 'react';
import { MdDelete } from 'react-icons/md';
import {
  HasButton,
  HasCompanyAccessInviteModal,
  HasDropdown,
  HasEmptyState,
  HasTable,
  HasText,
  HasTextInput,
  Notification,
} from '../../../components';
import i18n from '../../../i18n/config';
import { Company, CompanyDataViewGrant } from '../../../models';
import { CompanyService, UserService } from '../../../services';
import {
  CompanyAccessGrantInput,
  ConfirmMessages,
  ErrorMessages,
  Option,
  SORT_DIRECTIONS,
  SuccessMessages,
} from '../../../shared';
import { comparatorFn, displayPlaceholderIfEmpty, formatUserRole, getDateTime } from '../../../utils';

interface CompanyAccessSettingsState {
  companies: Company[];
  selectedCompanyId?: number;
  grants: CompanyDataViewGrant[];
  searchTerm: string;
  loadingCompanies: boolean;
  loadingGrants: boolean;
  showInviteModal: boolean;
}

const TABLE_ITEM_HEIGHT = 55;
const TABLE_HEADER_HEIGHT = 55;

class HasCompanyAccessSettings extends React.Component<{}, CompanyAccessSettingsState> {
  state: CompanyAccessSettingsState = {
    companies: [],
    selectedCompanyId: undefined,
    grants: [],
    searchTerm: '',
    loadingCompanies: true,
    loadingGrants: false,
    showInviteModal: false,
  };

  async componentDidMount() {
    try {
      const { data } = await CompanyService.getUserVisibleCompanies();
      this.setState({ companies: data, loadingCompanies: false });
    } catch (error) {
      this.setState({ loadingCompanies: false });
      this.notifyLoadError(error);
    }
  }

  loadGrants = async (companyId: number) => {
    this.setState({ loadingGrants: true });
    try {
      const { data } = await UserService.getCompanyDataViewGrantsByCompany(companyId);
      this.setState({ grants: data, searchTerm: '', loadingGrants: false });
    } catch (error) {
      this.setState({ grants: [], loadingGrants: false });
      this.notifyLoadError(error);
    }
  };

  shouldNotifyError = (error: any): boolean => {
    const status = error?.response?.status;
    return !!error?.response && status !== 401 && status !== 403;
  };

  notifyLoadError = (error: any) => {
    if (this.shouldNotifyError(error)) {
      Notification.error(ErrorMessages.UNEXPECTED_ERROR(error.response?.data?.message));
    }
  };

  handleCompanyChange = (companyId: number) => {
    this.setState({ selectedCompanyId: companyId });
    this.loadGrants(companyId);
  };

  grantAccess = async (input: CompanyAccessGrantInput): Promise<null> => {
    const results = await Promise.allSettled(
      input.companyIds.map((companyId) => UserService.createCompanyDataViewGrant(companyId, input.email))
    );

    let granted = 0;
    let skipped = 0;
    const failed: PromiseRejectedResult[] = [];
    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        result.value.data.length > 0 ? (granted += 1) : (skipped += 1);
      } else {
        failed.push(result);
      }
    });

    if (granted > 0 || skipped > 0) {
      Notification.success(SuccessMessages.COMPANY_ACCESS_GRANTED(input.email, granted, skipped));
    }
    const notifiableFailure = failed.map((result) => result.reason).find((reason) => this.shouldNotifyError(reason));
    if (notifiableFailure) {
      Notification.error(
        ErrorMessages.COMPANY_ACCESS_GRANT_FAILED(input.email, notifiableFailure.response?.data?.message)
      );
    }

    if (this.state.selectedCompanyId !== undefined) {
      await this.loadGrants(this.state.selectedCompanyId);
    }

    if (failed.length > 0) {
      return Promise.reject();
    }
    this.setState({ showInviteModal: false });
    return null;
  };

  revokeAccess = (grant: CompanyDataViewGrant) => {
    UserService.revokeCompanyDataViewGrant(grant.companyId, grant.invitedUserId).then(
      () => {
        Notification.success(SuccessMessages.COMPANY_ACCESS_REVOKED(grant.invitedUserName, grant.companyName));
        if (this.state.selectedCompanyId !== undefined) {
          this.loadGrants(this.state.selectedCompanyId);
        }
      },
      (error) => {
        if (this.shouldNotifyError(error)) {
          Notification.error(
            ErrorMessages.COMPANY_ACCESS_REVOKE_FAILED(grant.invitedUserName, error.response?.data?.message)
          );
        }
      }
    );
  };

  getVisibleGrants = (): CompanyDataViewGrant[] => {
    const term = this.state.searchTerm.toLocaleLowerCase();
    return this.state.grants.filter(
      (grant) =>
        grant.invitedUserName.toLocaleLowerCase().includes(term) ||
        grant.invitedUserEmail.toLocaleLowerCase().includes(term) ||
        grant.invitedUserRole.toLocaleLowerCase().includes(term) ||
        (grant.grantedByName || '').toLocaleLowerCase().includes(term)
    );
  };

  getColumns = (): ColumnProps<CompanyDataViewGrant>[] => [
    {
      title: i18n.t('shared.name'),
      dataIndex: 'invitedUserName',
      key: 'invitedUserName',
      align: 'center',
      sortDirections: SORT_DIRECTIONS.BOTH,
      defaultSortOrder: SORT_DIRECTIONS.ASCEND[0],
      sorter: (a, b) => comparatorFn<string>(a.invitedUserName, b.invitedUserName),
    },
    {
      title: i18n.t('shared.email'),
      dataIndex: 'invitedUserEmail',
      key: 'invitedUserEmail',
      align: 'center',
      ellipsis: true,
      sortDirections: SORT_DIRECTIONS.BOTH,
      sorter: (a, b) => comparatorFn<string>(a.invitedUserEmail, b.invitedUserEmail),
    },
    {
      title: i18n.t('shared.role'),
      key: 'invitedUserRole',
      align: 'center',
      render: (_, record) => formatUserRole(record.invitedUserRole),
    },
    {
      title: i18n.t('settings.allowedBy'),
      key: 'grantedByName',
      align: 'center',
      render: (_, record) => displayPlaceholderIfEmpty(record.grantedByName),
    },
    {
      title: i18n.t('settings.allowedOn'),
      key: 'grantedAt',
      align: 'center',
      render: (_, record) =>
        record.grantedAt ? getDateTime(record.grantedAt, { day: 'numeric', month: 'long', year: 'numeric' }) : '-',
    },
    {
      title: i18n.t('shared.actions'),
      key: 'actions',
      align: 'center',
      render: (_, record) => (
        <Popconfirm
          title={ConfirmMessages.REVOKE_COMPANY_ACCESS(record.invitedUserName, record.companyName)}
          okText={i18n.t('shared.yes')}
          cancelText={i18n.t('shared.no')}
          onConfirm={() => this.revokeAccess(record)}
          placement="topLeft"
          style={{ maxWidth: '200px' }}
        >
          <MdDelete style={{ fontSize: '18px' }} className="icon-hover danger" />
        </Popconfirm>
      ),
    },
  ];

  render() {
    const { companies, selectedCompanyId, searchTerm, loadingCompanies, loadingGrants, showInviteModal } = this.state;
    const visibleGrants = this.getVisibleGrants();
    return (
      <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <HasDropdown<Company>
              size="large"
              loading={loadingCompanies}
              value={selectedCompanyId}
              placeholder={i18n.t('companyAccess.selectCompany')}
              style={{ minWidth: '260px', marginRight: '20px' }}
              onChange={(value) => this.handleCompanyChange(value as number)}
              options={companies.map<Option<Company>>((company) => ({
                value: company.id,
                label: company.name,
                forObject: company,
              }))}
            />
            <HasTextInput
              prefix={<SearchOutlined />}
              placeholder={i18n.t('shared.search')}
              value={searchTerm}
              onChange={(e) => this.setState({ searchTerm: e.target.value })}
              style={{ maxWidth: '180px', marginRight: '20px' }}
              size="large"
              disabled={selectedCompanyId === undefined}
            />
            <Skeleton loading={loadingGrants} active paragraph={false} title={{ width: '150px' }}>
              <HasText
                content={i18n.t('dataDisplay.displayingResults', { count: visibleGrants.length })}
                style={{ fontSize: '12px' }}
              />
            </Skeleton>
          </div>
          <HasButton
            onClick={() => this.setState({ showInviteModal: true })}
            type="primary"
            disabled={loadingCompanies}
            icon={<PlusOutlined />}
            style={{ minWidth: 0 }}
          />
        </div>
        {selectedCompanyId === undefined ? (
          <div className="d-flex flex-grow-1 align-items-center justify-content-center">
            <HasEmptyState>
              <HasText content={i18n.t('companyAccess.noCompanySelected')} strong type="secondary" />
            </HasEmptyState>
          </div>
        ) : (
          <HasTable<CompanyDataViewGrant>
            rowKey={(record) => `${record.companyId}-${record.invitedUserId}`}
            data={visibleGrants}
            columns={this.getColumns()}
            loading={loadingGrants}
            tableHeaderHeight={TABLE_HEADER_HEIGHT}
            tableItemHeight={TABLE_ITEM_HEIGHT}
            useScroll
          />
        )}
        <HasCompanyAccessInviteModal
          visible={showInviteModal}
          companies={companies}
          onOk={this.grantAccess}
          onCancel={() => this.setState({ showInviteModal: false })}
        />
      </div>
    );
  }
}

export default HasCompanyAccessSettings;
